// src/components/TablaColecta.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from "../style/Transportadora.module.css";

const JSON_COLECTA_URL = "https://marketbusters.pro/Dropdrop/pedidos_listado_preparando_guia.json";

// ==== Utils ====
const cleanUbigeo = (input) => {
  let s = String(input || "");
  s = s.replace(/regi[oó]n\s+metropolitana\s+de\s+santiago,\s*/iu, "");
  s = s.trim();

  const toTitle = (str) =>
    str
      .toLowerCase()
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");

  const plain = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const fixes = {
    nunoa: "Ñuñoa",
    maipu: "Maipú",
    "san joaquin": "San Joaquín",
    "estacion central": "Estación Central",
    conchali: "Conchalí",
  };
  if (fixes[plain]) return fixes[plain];
  return toTitle(s);
};

const formatCLP = (val) => {
  const n = Number(String(val).replace(/[^0-9.-]/g, ""));
  if (!isFinite(n)) return String(val ?? "");
  return new Intl.NumberFormat("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(Math.round(n));
};

const TrackingChip = ({ value }) => {
  const v = String(value || "").toLowerCase().trim();
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "9999px",
    fontSize: 12,
    fontWeight: 800,
    color: "#fff",
    border: "1px solid #000",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  };
  if (v.includes("prepar")) return <span style={{ ...base, background: "#10b981" }}>Preparando</span>;
  if (v.includes("guia"))   return <span style={{ ...base, background: "#0ea5e9" }}>Guía</span>;
  return <span style={{ ...base, background: "#6b7280" }}>{value || "—"}</span>;
};

export default function TablaColecta({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Orden jerárquico: 'proveedor' | 'comuna'
  const [modo, setModo] = useState("proveedor");

  // Expand/Collapse (se separa por modo para no mezclar estados)
  const [openTop, setOpenTop] = useState({});
  const [openSub, setOpenSub] = useState({});

  // Colores neutros
  const borderMain = "#2a2f3d";
  const borderInner = "#262b3a";
  const bgTop = "#0b0e14";
  const bgSub = "#0e1119";

  // Cargar JSON cada vez que refreshKey cambie
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await fetch(JSON_COLECTA_URL, { cache: "no-store", mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.items) ? data.items : [];
        if (!abort) setItems(list);
      } catch (e) {
        if (!abort) setErr(`No se pudo cargar: ${e.message}`);
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => void (abort = true);
  }, [refreshKey]);

  // Agrupación por PROVEEDOR → COMUNA
  const groupedByProveedor = useMemo(() => {
    const map = new Map(); // prov => { total, count, sub: Map }
    for (const it of items) {
      const prov = (it?.nombreNegocioProveedor || "—").trim() || "—";
      const comuna = cleanUbigeo(it?.ubigeoComprador);

      if (!map.has(prov)) map.set(prov, { total: 0, count: 0, sub: new Map() });
      const p = map.get(prov);
      p.count += 1;
      p.total += Number(String(it?.total || "0").replace(/[^0-9.-]/g, "")) || 0;

      if (!p.sub.has(comuna)) p.sub.set(comuna, { total: 0, count: 0, rows: [] });
      const c = p.sub.get(comuna);
      c.count += 1;
      c.total += Number(String(it?.total || "0").replace(/[^0-9.-]/g, "")) || 0;
      c.rows.push(it);
    }

    const ordered = [];
    for (const [prov, data] of Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], "es"))) {
      const subs = Array.from(data.sub.entries()).sort((a, b) => a[0].localeCompare(b[0], "es"));
      ordered.push({ topKey: prov, topLabel: prov, total: data.total, count: data.count, subs });
    }
    return ordered;
  }, [items]);

  // Agrupación por COMUNA → PROVEEDOR
  const groupedByComuna = useMemo(() => {
    const map = new Map(); // comuna => { total, count, sub: Map }
    for (const it of items) {
      const prov = (it?.nombreNegocioProveedor || "—").trim() || "—";
      const comuna = cleanUbigeo(it?.ubigeoComprador);

      if (!map.has(comuna)) map.set(comuna, { total: 0, count: 0, sub: new Map() });
      const c = map.get(comuna);
      c.count += 1;
      c.total += Number(String(it?.total || "0").replace(/[^0-9.-]/g, "")) || 0;

      if (!c.sub.has(prov)) c.sub.set(prov, { total: 0, count: 0, rows: [] });
      const p = c.sub.get(prov);
      p.count += 1;
      p.total += Number(String(it?.total || "0").replace(/[^0-9.-]/g, "")) || 0;
      p.rows.push(it);
    }

    const ordered = [];
    for (const [comu, data] of Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], "es"))) {
      const subs = Array.from(data.sub.entries()).sort((a, b) => a[0].localeCompare(b[0], "es"));
      ordered.push({ topKey: comu, topLabel: comu, total: data.total, count: data.count, subs });
    }
    return ordered;
  }, [items]);

  const dataToRender = modo === "proveedor" ? groupedByProveedor : groupedByComuna;

  const toggleTop = (key) => setOpenTop((s) => ({ ...s, [`${modo}::${key}`]: !s[`${modo}::${key}`] }));
  const toggleSub = (topKey, subKey) =>
    setOpenSub((s) => ({ ...s, [`${modo}::${topKey}||${subKey}`]: !s[`${modo}::${topKey}||${subKey}`] }));

  const isTopOpen = (key) => !!openTop[`${modo}::${key}`];
  const isSubOpen = (topKey, subKey) => !!openSub[`${modo}::${topKey}||${subKey}`];

  // Cuando cambia el modo, colapsamos para no confundir
  useEffect(() => {
    setOpenTop({});
    setOpenSub({});
  }, [modo]);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader} style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h3 className={styles.cardTitle} style={{ marginRight: "auto" }}>
          {/* Título + contador total de pedidos */}
          Tabla Colecta — (Preparando / Guía){items.length > 0 && ` · ${items.length} PEDIDOS`}
        </h3>

        {/* Botones de orden */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className={styles.tabBtn}
            onClick={() => setModo("proveedor")}
            style={{ ...(modo === "proveedor" ? { background: "#1f2937" } : {}) }}
            title="Mostrar Proveedor → Comuna → Filas"
          >
            Ordenar por Proveedor
          </button>
          <button
            className={styles.tabBtn}
            onClick={() => setModo("comuna")}
            style={{ ...(modo === "comuna" ? { background: "#1f2937" } : {}) }}
            title="Mostrar Comuna → Proveedor → Filas"
          >
            Ordenar por Comuna
          </button>
        </div>

        <div className={styles.cardSub}>
          {loading && <span>Cargando…</span>}
          {err && <span className={styles.error}>{err}</span>}
        </div>
      </div>

      <div className={styles.tableScroll} style={{ padding: 0 }}>
        {dataToRender.length === 0 ? (
          <div className={styles.empty} style={{ padding: 24 }}>
            {loading ? "Cargando datos…" : "Sin resultados."}
          </div>
        ) : (
          dataToRender.map((top) => {
            const topOpen = isTopOpen(top.topKey);
            return (
              <div key={top.topKey} style={{ borderTop: `1px solid ${borderMain}` }}>
                {/* Cabecera de nivel superior */}
                <button
                  onClick={() => toggleTop(top.topKey)}
                  className={styles.rowBtn}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    background: bgTop,
                    color: "#e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 700,
                    border: `1px solid ${borderMain}`,
                    borderLeft: `3px solid ${borderMain}`,
                    outline: "none",
                    boxShadow: "none",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{topOpen ? "▾" : "▸"}</span>
                    {top.topLabel}
                    <span style={{ opacity: 0.7, fontWeight: 600 }}>· {top.count} pedidos</span>
                  </span>
                  <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                    ${formatCLP(top.total)}
                  </span>
                </button>

                {/* Nivel secundario */}
                {topOpen && (
                  <div style={{ paddingLeft: 28 }}>
                    {top.subs.map(([subKey, subData]) => {
                      const subOpen = isSubOpen(top.topKey, subKey);
                      return (
                        <div key={`${top.topKey}||${subKey}`} style={{ borderTop: `1px dashed ${borderInner}` }}>
                          <button
                            onClick={() => toggleSub(top.topKey, subKey)}
                            className={styles.rowBtn}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              padding: "10px 14px",
                              background: bgSub,
                              color: "#e5e7eb",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              fontWeight: 600,
                              border: `1px solid ${borderInner}`,
                              borderLeft: `3px solid ${borderInner}`,
                              outline: "none",
                              boxShadow: "none",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 16 }}>{subOpen ? "▾" : "▸"}</span>
                              {subKey}
                              <span style={{ opacity: 0.7, fontWeight: 600 }}>· {subData.count}</span>
                            </span>
                            <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                              ${formatCLP(subData.total)}
                            </span>
                          </button>

                          {/* Filas */}
                          {subOpen && (
                            <div style={{ padding: "8px 12px 16px 18px" }}>
                              <div className={styles.tableScroll} style={{ maxHeight: 420 }}>
                                <table className={styles.table}>
                                  <thead className={styles.thead}>
                                    <tr>
                                      <th className={styles.th}>ID</th>
                                      <th className={styles.th}>Fecha</th>
                                      <th className={styles.th}>Comprador</th>
                                      <th className={styles.th}>Celular</th>
                                      <th className={styles.th}>Dirección</th>
                                      <th className={styles.th}>Total</th>
                                      <th className={styles.th}>Tracking</th>
                                      <th className={styles.th}>Guía</th>
                                    </tr>
                                  </thead>
                                  <tbody className={styles.tbody}>
                                    {subData.rows.map((row, i) => (
                                      <tr key={`${row.id || "r"}-${i}`} className={styles.tr}>
                                        <td className={styles.td}>{row.id || "—"}</td>
                                        <td className={styles.td}>{row.fechaEmision || "—"}</td>
                                        <td className={styles.td}>{row.nombreComprador || "—"}</td>
                                        <td className={styles.td}>
                                          {row.celularComprador ? (
                                            <a
                                              href={`https://wa.me/${row.celularComprador}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className={styles.link}
                                            >
                                              {row.celularComprador}
                                            </a>
                                          ) : "—"}
                                        </td>
                                        <td className={styles.td}>{row.direccionComprador || "—"}</td>
                                        <td className={styles.td}>${formatCLP(row.total)}</td>
                                        <td className={styles.td}><TrackingChip value={row.trackindPedido} /></td>
                                        <td className={styles.td}>{row.idGuia || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
